import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, ArrowRight, Globe, Cpu, Users, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LocalizedLink } from "@/components/layout/LocalizedLink";

const solutions = [
  { titleKey: "nav.governmentSolutions", descKey: "nav.solutionsDesc.government", href: "/government-solutions", icon: Building2 },
  { titleKey: "nav.technologyPlatform", descKey: "nav.solutionsDesc.technology", href: "/technology-platform", icon: Cpu },
  { titleKey: "nav.operationalDelivery", descKey: "nav.solutionsDesc.operations", href: "/operational-delivery", icon: Globe },
  { titleKey: "nav.tourismTrade", descKey: "nav.solutionsDesc.tourism", href: "/tourism-trade-support", icon: Users },
];

const navLinks = [
  { labelKey: "nav.globalPresence", href: "/global-presence" },
  { labelKey: "nav.caseStudies", href: "/case-studies" },
  { labelKey: "nav.resources", href: "/resources" },
  { labelKey: "nav.security", href: "/security" },
  { labelKey: "nav.about", href: "/about" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang } = useParams<{ lang: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => {
    if (typeof window === 'undefined') return false;
    const currentPath = window.location.pathname;
    // For English (no lang param), check direct path
    // For other languages, check with prefix
    const fullHref = lang ? `/${lang}${href}` : href;
    return currentPath === fullHref || currentPath === href;
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-background/95 backdrop-blur-xl border-b border-border shadow-md"
          : "bg-primary"
      )}
      style={{ height: '72px' }}
    >
      <div className="container-premium h-full">
        <nav className="flex items-center justify-between h-full gap-2">
          {/* Logo - HIGH CONTRAST ALWAYS VISIBLE */}
          <LocalizedLink to="/" className="flex items-center gap-3 group flex-shrink-0">
            {/* VX Monogram */}
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
              isScrolled ? "bg-accent" : "bg-primary-foreground"
            )}>
              <span className={cn(
                "font-bold text-base tracking-tight font-display",
                isScrolled ? "text-accent-foreground" : "text-primary"
              )}>VX</span>
            </div>
            {/* Wordmark - ALWAYS VISIBLE AND HIGH CONTRAST */}
            <div className="flex flex-col">
              <span className={cn(
                "font-display font-bold text-xl leading-tight tracking-tight transition-colors whitespace-nowrap",
                isScrolled ? "text-foreground" : "text-primary-foreground"
              )}>
                VisExperience
              </span>
              <span className={cn(
                "text-[11px] font-medium uppercase tracking-widest leading-tight transition-colors hidden sm:block whitespace-nowrap",
                isScrolled ? "text-muted-foreground" : "text-primary-foreground/80"
              )}>
                {t('common.governmentServices')}
              </span>
            </div>
          </LocalizedLink>

          {/* Desktop Navigation - Use 2xl breakpoint for long language labels */}
          <div className="hidden 2xl:flex items-center gap-1 flex-shrink-0">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={cn(
                    "h-10 px-3 text-sm font-medium rounded-lg transition-colors",
                    isScrolled 
                      ? "bg-transparent hover:bg-muted text-foreground data-[state=open]:bg-muted" 
                      : "bg-transparent hover:bg-primary-foreground/15 text-primary-foreground data-[state=open]:bg-primary-foreground/15"
                  )}>
                    {t('nav.solutions')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[500px] gap-2 p-4 md:grid-cols-2 bg-popover rounded-xl border border-border shadow-xl">
                      {solutions.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <LocalizedLink 
                              to={item.href} 
                              className={cn(
                                "block select-none rounded-lg p-3.5 leading-none no-underline outline-none transition-all hover:bg-muted",
                                isActive(item.href) && "bg-muted"
                              )}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                                  <item.icon className="h-5 w-5 text-accent" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-foreground">{t(item.titleKey)}</div>
                                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t(item.descKey)}</p>
                                </div>
                              </div>
                            </LocalizedLink>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {navLinks.map((link) => (
              <LocalizedLink
                key={link.href}
                to={link.href}
                className={cn(
                  "h-10 px-3 inline-flex items-center text-sm font-medium rounded-lg transition-colors",
                  isScrolled
                    ? "hover:bg-muted text-foreground/80 hover:text-foreground"
                    : "hover:bg-primary-foreground/15 text-primary-foreground/90 hover:text-primary-foreground",
                  isActive(link.href) && (isScrolled ? "text-foreground bg-muted" : "text-primary-foreground bg-primary-foreground/15")
                )}
              >
                {t(link.labelKey)}
              </LocalizedLink>
            ))}
          </div>

          {/* Desktop CTA + Language */}
          <div className="hidden 2xl:flex items-center gap-3 flex-shrink-0">
            <LanguageSelector variant="compact" />
            <Button asChild size="default" className="h-10 px-5 rounded-lg bg-accent hover:bg-accent/90 text-accent-foreground font-medium shadow-sm">
              <LocalizedLink to="/contact">
                {t('common.partnerWithUs')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </LocalizedLink>
            </Button>
          </div>

          {/* Tablet/Desktop Hamburger (lg to 2xl) */}
          <div className="hidden lg:flex 2xl:hidden items-center gap-2 flex-shrink-0">
            <LanguageSelector variant="compact" />
            <Button asChild size="sm" className="h-9 px-4 rounded-lg bg-accent hover:bg-accent/90 text-accent-foreground font-medium shadow-sm">
              <LocalizedLink to="/contact">
                {t('common.partnerWithUs')}
              </LocalizedLink>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  aria-label={t('nav.openMenu')}
                  className={cn(
                    "h-10 w-10 rounded-lg",
                    isScrolled ? "text-foreground hover:bg-muted" : "text-primary-foreground hover:bg-primary-foreground/15"
                  )}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full max-w-xs p-0 bg-background border-l border-border">
                <MobileNav setMobileOpen={setMobileOpen} t={t} />
              </SheetContent>
            </Sheet>
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector variant="compact" />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  aria-label={t('nav.openMenu')}
                  className={cn(
                    "h-10 w-10 rounded-lg",
                    isScrolled ? "text-foreground hover:bg-muted" : "text-primary-foreground hover:bg-primary-foreground/15"
                  )}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full max-w-xs p-0 bg-background border-l border-border">
                <MobileNav setMobileOpen={setMobileOpen} t={t} />
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}

// Extracted mobile nav for cleaner code
function MobileNav({ setMobileOpen, t }: { setMobileOpen: (open: boolean) => void; t: (key: string) => string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Mobile Header */}
      <div className="flex items-center gap-3 p-5 border-b border-border">
        <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
          <span className="text-accent-foreground font-bold text-sm font-display">VX</span>
        </div>
        <span className="font-display font-bold text-lg text-foreground">VisExperience</span>
      </div>
      
      {/* Mobile Nav */}
      <div className="flex-1 overflow-y-auto p-5">
        <div className="mb-6">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">{t('nav.solutions')}</p>
          <div className="space-y-1">
            {solutions.map((item) => (
              <LocalizedLink 
                key={item.href} 
                to={item.href} 
                onClick={() => setMobileOpen(false)} 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                  <item.icon className="h-4 w-4 text-accent" />
                </div>
                <div className="text-sm font-medium text-foreground">{t(item.titleKey)}</div>
              </LocalizedLink>
            ))}
          </div>
        </div>
        
        <div className="h-px bg-border mb-5" />
        
        <div className="space-y-1">
          {navLinks.map((link) => (
            <LocalizedLink 
              key={link.href} 
              to={link.href} 
              onClick={() => setMobileOpen(false)} 
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              {t(link.labelKey)}
            </LocalizedLink>
          ))}
        </div>
      </div>
      
      {/* Mobile CTA */}
      <div className="p-5 border-t border-border">
        <Button asChild className="w-full h-11 rounded-lg bg-accent hover:bg-accent/90 font-medium">
          <LocalizedLink to="/contact" onClick={() => setMobileOpen(false)}>
            {t('common.partnerWithUs')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </LocalizedLink>
        </Button>
      </div>
    </div>
  );
}
